// ABOUTME: Categorical palettes for Crossing's rung-3 components (the tracker, the runner plate, gold paddles).
// ABOUTME: Plain constants, not theme-integrated -- these own their CSS per ADR #14 (Issue #1940).

import type { CrossingPillState } from '../types/api';

// Header/plate/Steersman grounds and golds, per design-language.md:81, :85, :87.
export const CROSSING_GROUND = '#FCF8F1';
export const CROSSING_HAIRLINE = '#EAD9B9';
export const CROSSING_STEERSMAN_GROUND = '#F8F1E4';
export const CROSSING_GOLD = '#D9A441';
export const CROSSING_GOLD_INK = '#8A6A2E';
export const CROSSING_TEAL_EYEBROW = '#46776F';

// Rung-2 filled-button colors, per ADR #14 (a stock nalu button renders black-on-teal in Crossing).
export const CROSSING_BUTTON_FILL = '#3E6B60'; // matches CROSSING_TEAL_BAR
export const CROSSING_BUTTON_FILL_HOVER = '#2E4B45'; // matches CROSSING_HEADLINE_INK
export const CROSSING_BUTTON_TEXT = '#F7EFDC';

// The v28 shell (header, console card, tracker, picker, pill buttons), per design-v28/TOKENS.md. CROSSING_GOLD stays for
// the callers that already use it; CROSSING_GOLD_V28 is the shell's `gold`.
export const CROSSING_GOLD_V28 = '#D2B06A';
export const CROSSING_GOLD_BTN = '#C9A05A';
export const CROSSING_GOLD_BTN_HOVER = '#B8903F';
export const CROSSING_INK_STRONG = '#2E2A25';
export const CROSSING_BORDER_PILL = '#D9C9A8';
export const CROSSING_BORDER_HEADER = '#EAD9B9';
export const CROSSING_PAPER = '#FCF8F1';
export const CROSSING_PAPER_94 = 'rgba(252,248,241,0.94)';
export const CROSSING_TEAL_BAR = '#3E6B60';
export const CROSSING_TAN_SEEN = '#C9B896';
export const CROSSING_TAN_IDLE = '#E9DFC9';
export const CROSSING_TEAL_TEXT_ON = '#F7EFDC';
export const CROSSING_INK_MUTED = '#6E6258'; // 5.59:1 on paper, so the console sub uses it rather than the 3.89:1 ink-sub
export const CROSSING_HEADER_EYEBROW_INK = '#3E3A34';
export const CROSSING_PILL_SHADOW = '0 3px 10px rgba(76,65,56,0.2)';
export const CROSSING_CARD_SHADOW = '0 6px 16px rgba(46,75,69,0.14)';
export const CROSSING_FOCUS_RING = '#3E6B60'; // the visible keyboard outline on header pills, tracker arrows, picker arrows and buttons

// Tracker bar colours by pill state: a phase the child has passed looks passed, whether it was done or skipped.
export const TRACKER_BAR_COLORS: Record<CrossingPillState, string> = {
  current: CROSSING_TEAL_BAR,
  done: CROSSING_TAN_SEEN,
  upcoming: CROSSING_TAN_IDLE,
  skipped: CROSSING_TAN_SEEN,
  'skipped-ready': CROSSING_TAN_SEEN,
};

// Feeling-mode marker hues, per design-language.md:4337 (coreHue map).
export const FEELING_HUES: Record<string, string> = {
  happy: '#D9B952',
  excited: '#DF9249',
  surprised: '#5DADB2',
  scared: '#6B7096',
  sad: '#708FAC',
  worried: '#9787B1',
  yucky: '#87966B',
  angry: '#C7675A',
};

// Reading-carousel icon fills (S25 correction, #1945: flat single-item carousel, no ring).
export const CROSSING_NEUTRAL_FILL = '#E0D6C0'; // fallback icon fill when a feeling has no hue entry
export const CROSSING_WEATHER_MARKER = '#CFE2DC'; // weather-mode icon fill (distinct from feeling hues)

// OceanScene backdrop, ported from the prototype's weather-reading stage (Crossing Prototype
// v25.dc.html:1858-1866) -- cream sky over a two-depth sea with parallax wave bands.
export const CROSSING_SKY_CREAM = '#FBF4E6';
export const CROSSING_RESET_MASK = 'rgba(52,62,74,0.48)'; // the "RESET FOR" mask over the storm (v28)
export const CROSSING_RESET_MASK_TEXT_SHADOW = 'rgba(20,30,40,0.4)';
export const CROSSING_SEA_PALE = '#E6F2EE';
export const CROSSING_SEA_MID = '#CFE2DC';
export const CROSSING_SEA_DEEP = '#A9CFC6';
export const CROSSING_SEA_FOREGROUND = '#8FBDB2'; // the nearest 5C swell band and the water under it
export const CROSSING_HORIZON_LINE = 'rgba(70,119,111,0.3)';
// v28 deck scene (TOKENS.md): the Feeling sky on 2A, the app page colour, and the deck sprite's hull, wood and sail.
export const CROSSING_FEELING_SKY_TOP = '#EEF1EC'; // the 2A Feeling sky gradient, top (SPEC 3)
export const CROSSING_FEELING_SKY_BOTTOM = '#F7F1E3'; // the 2A Feeling sky gradient, bottom (SPEC 3)
// The canoe asset's rigging strokes (1b-canoe.svg), which DeckRigging continues above the asset's top edge.
export const CROSSING_DECK_RIGGING = 'rgba(252,248,241,0.55)';
export const CROSSING_DECK_RIGGING_STRONG = 'rgba(252,248,241,0.6)';
// The grey-blue wash over the 2A sky: light enough that CROSSING_HEADLINE_INK keeps 4.5:1 behind the copy and the name.
export const CROSSING_FEELING_STORM_OVERLAY = 'rgba(86,96,112,0.3)';
export const CROSSING_PAGE = '#F1E7D3'; // TOKENS.md page: app background behind the frame
export const CROSSING_HULL_LIT = '#6E4D38'; // TOKENS.md hull-lit
export const CROSSING_HULL_DARK = '#4A3024'; // TOKENS.md hull-dark
export const CROSSING_HULL_EDGE = '#3E2419'; // TOKENS.md hull-edge
export const CROSSING_WOOD = ['#C9A574', '#B08A55', '#8E6B46'] as const; // TOKENS.md wood: beams, plank seams
export const CROSSING_SAIL = ['#C97A5C', '#B9664A'] as const; // TOKENS.md sail: crab-claw sails
export const CROSSING_SAIL_STROKE = '#8E4A33'; // TOKENS.md sail stroke

// Feeling-face SVG (Crossing Prototype v25.dc.html:1870-1879) -- the circle's cream fill and the
// ink used for every brow/eye/mouth stroke, shared across all 8 feelings.
export const CROSSING_FACE_FILL = '#FFF8EC';
export const CROSSING_FACE_STROKE = '#244E4A';

// The runner plate -- the floating bottom-shelf card (Crossing Prototype v25.dc.html:2980-3009):
// card chrome, the 20px history-arrow circles, and row-2 text inks.
export const CROSSING_PLATE_BG = 'rgba(252,248,241,0.95)';
// Hold's veil over the scene: cream, solid enough that CROSSING_HEADLINE_INK clears 4.5:1 while the cloud stack
// still shows faintly behind it.
export const CROSSING_HOLD_VEIL = 'rgba(251,246,234,0.72)';
// The Make It Right task: its left border and its "New" tag (TOKENS.md coral, coral-soft).
export const CROSSING_CORAL = '#C05F47';
export const CROSSING_CORAL_SOFT = '#F9F1EC';
export const CROSSING_PLATE_BORDER = '#E3D3AF';
export const CROSSING_PLATE_SHADOW = '0 6px 16px rgba(46,75,69,0.14)';
export const CROSSING_ARROW_BORDER = '#E8DCC2';
export const CROSSING_ARROW_INK = '#6E6258';
export const CROSSING_HEADLINE_INK = '#2E4B45';
export const CROSSING_SUB_INK = '#8A7B66';

// Rung-2 secondary-pill colors (the duration picker, Issue #1943), per design-language.md:86.
// Hover reuses CROSSING_STEERSMAN_GROUND, which already matches the secondary-pill hover exactly.
export const CROSSING_PILL_BG = '#FCF8F1';
export const CROSSING_PILL_BORDER = '#D9C9A8';
export const CROSSING_PILL_INK = '#4C4138';

// Breath-disc ring track (Issue #1943), prototype :1204.
export const CROSSING_RING_TRACK = '#A89F90';

// The cloud stack's own fills are per-feeling (screens/clouds/cloudPalette.ts). STORM is the fill of the
// Better Question cloud on 5B (Issue #1946, bryan-provided mockup); INK is the copy ink on every cloud. GHOST, REVEAL and
// FEELING are the fills of the plain clouds (screens/clouds/tierBlobs.tsx) that size the 3A entrance's rows.
export const CROSSING_CLOUD_GHOST = '#E3DED6';
export const CROSSING_CLOUD_REVEAL = '#9A9B9E';
export const CROSSING_CLOUD_FEELING = '#E4D9AE';
export const CROSSING_CLOUD_STORM = '#C9BFA3';
export const CROSSING_CLOUD_WHITE = '#FFFFFF';
export const CROSSING_CLOUD_INK = '#2E3138';
// The weather response on the head cloud: title and label inks, and the white halo that lifts them off the slate
// (Cloud Stack Build Spec section 5; their contrast is tracked in #2130), and the icon tile's border on the base.
export const CROSSING_HEAD_TITLE_INK = '#45291F';
export const CROSSING_HEAD_LABEL_INK = '#1F2430';
export const CROSSING_HEAD_HALO = 'drop-shadow(0 0 5px rgba(255,255,255,.35)) drop-shadow(0 0 12px rgba(255,255,255,.22))';
export const CROSSING_ICON_TILE_BORDER = 'rgba(76,65,56,0.4)';

// Too Much quadrant petal colors (Issue #1943, seam for #1944), prototype tmkDefs :3253.
// bg/border/ink/dark per category code (B=Body Needs, A=Too Much At Once, S=Senses Full,
// F=Feelings Piled Up), matching the prototype's soft/ink/dark palette exactly.
export const TOOMUCH_B_BG = '#F7E7DF';
export const TOOMUCH_B_BORDER = '#D8A793';
export const TOOMUCH_B_INK = '#B8654A';
export const TOOMUCH_B_DARK = '#7A3A28';
export const TOOMUCH_A_BG = '#E3EFEA';
export const TOOMUCH_A_BORDER = '#9EC4BA';
export const TOOMUCH_A_INK = '#3E7A70';
export const TOOMUCH_A_DARK = '#2E5B53';
export const TOOMUCH_S_BG = '#F6ECD3';
export const TOOMUCH_S_BORDER = '#D5BA6E';
export const TOOMUCH_S_INK = '#8A6A2E';
export const TOOMUCH_S_DARK = '#6E5423';
export const TOOMUCH_F_BG = '#F8E4E6';
export const TOOMUCH_F_BORDER = '#DBA6AD';
export const TOOMUCH_F_INK = '#C05F6B';
export const TOOMUCH_F_DARK = '#8F3D4A';

// Breathing-strategy center circle (Issue #1943), prototype :1232-1246.
export const CROSSING_CIRCLE_BORDER = '#C9AC5C';
export const CROSSING_CIRCLE_READY_BG = '#F4F8F4';

// Selection-chip focus ring (design-language.md §3), shared by any Crossing tile/chip toggle.
export const CROSSING_FOCUS_OUTLINE = '#B5853C';

// BreathScene backdrop (Issue #1943), prototype :1253 -- the breathe step's own clear-sky gradient
// is blue, distinct from OceanScene's cream sky (bryan, manual-test: "this is not the background
// that the prototype had"). Sea/hill colors below the horizon reuse CROSSING_SEA_MID/_DEEP, which
// already match the prototype's #CFE2DC/#A9CFC6 exactly.
export const CROSSING_BREATH_SKY_TOP = '#9FD3EC';
export const CROSSING_BREATH_SKY_MID = '#BFE2F2';
export const CROSSING_BREATH_SKY_BOTTOM = '#DDEFF6';

// Breathing round weather (Issue #2123): the storm the round starts in, the overcast it passes through, the cloud
// inks and the rain, from the Reset reference (docs/plans/crossing/evidence/breath-reset-reference/excerpts.md).
export const CROSSING_STORM_SKY_TOP = '#6C7583';
export const CROSSING_STORM_SKY_BOTTOM = '#8C949D';
export const CROSSING_OVERCAST_SKY_TOP = '#C9CFD6';
export const CROSSING_OVERCAST_SKY_BOTTOM = '#E2E5E8';
export const CROSSING_CLOUD_INK_DARK = '#5E6773';
export const CROSSING_CLOUD_INK_LIGHT = '#FBFDFE';
export const CROSSING_BREATH_STAGE_UPPER = '#FBF4E6'; // behind the sky, top half
export const CROSSING_BREATH_STAGE_LOWER = '#E6F2EE'; // the pale band above the sea, bottom half
// The sun scene a first arrival opens on (its sky gradient, sun disc with halo, and white clouds) and the storm sky that settles over it (v28 SPEC 1, TRANSITIONS 1).
export const CROSSING_SUN_SCENE_GRADIENT_TOP = '#FBF4E6';
export const CROSSING_SUN_SCENE_GRADIENT_MID = '#F6F1E3';
export const CROSSING_SUN_SCENE_GRADIENT_BOTTOM = '#E9F0E9';
export const CROSSING_SUN_SCENE_SUN = '#F3E2A6';
export const CROSSING_SUN_SCENE_SUN_HALO = 'rgba(243,226,166,0.35)';
export const CROSSING_SUN_SCENE_SUN_GLOW = 'rgba(243,226,166,0.5)';
export const CROSSING_SUN_SCENE_CLOUD_FILL = 'rgba(255,255,255,0.8)';
export const CROSSING_SUN_SCENE_CLOUD_FILL_SOFT = 'rgba(255,255,255,0.65)';
// The storm sky's 260 degree gradient: each colour and where it sits along the gradient line, 0 to 1.
export const CROSSING_STORM_SKY_STOPS = [['#7C8390', 0], ['#8E95A1', 0.42], ['#B3BFC6', 0.72], ['#D9E1E2', 1]] as const;
export const CROSSING_STORM_SKY_GLOW = 'radial-gradient(ellipse at 50% 50%, rgba(252,243,222,0.85) 0%, rgba(252,243,222,0.35) 38%, rgba(252,243,222,0) 70%)';
export const CROSSING_STORM_CLOUD_FILL = { dark: '#5E6676', mid: '#656D7D', light: '#7E8795', pale: '#AEB7BF' };
export const CROSSING_STORM_VEIL_DROP = 'rgba(240,244,246,0.32)';
export const CROSSING_BREATH_SUN = 'radial-gradient(circle, #F7CC85 0 32%, rgba(247,213,150,0.5) 32% 48%, rgba(249,228,187,0.36) 48% 66%, rgba(250,238,214,0) 78%)';

// Sky scene behind 5C and 5D, and the 5C guiding truth card (prototype v25 kala stage).
export const CROSSING_SUN_FILL = '#EFD9A0';
export const CROSSING_SUN_GLOW = '0 0 22px 10px rgba(239,217,160,0.55)';
export const CROSSING_SKY_FORGIVEN_TOP = '#BFE3F2'; // sky-forgiven, the 5C sky from the top of the scene to the horizon
export const CROSSING_SKY_FORGIVEN_BOTTOM = '#D9F0FA';
// The forgiven sky drawn from the header to the horizon on 4B and 5B: the two stops, then the bottom stop fading out (the
// prototype's wxSky layer). The trailing 00 is the bottom color's own alpha.
export const CROSSING_SKY_FORGIVEN_GRADIENT = `linear-gradient(${CROSSING_SKY_FORGIVEN_TOP} 0%, ${CROSSING_SKY_FORGIVEN_BOTTOM} 78%, ${CROSSING_SKY_FORGIVEN_BOTTOM}00 100%)`;
export const CROSSING_STAR_INK = '#B5853C';
export const CROSSING_TRUTH_CARD_GROUND = 'rgba(252,248,241,0.92)';
export const CROSSING_TRUTH_CARD_BORDER = '#D9C9A8';

// The 3A weather entrance (Issue #2128): the darker skyline, the rain streaks, and the reach tiles. Values read from the
// rain reference (docs/plans/crossing/evidence/impact-rain-reference).
export const CROSSING_IMPACT_SKY_MASK = 'rgba(90,78,66,0.3)';
export const CROSSING_RAIN_STREAK = 'rgb(96,126,166)';
// The reference's tiles are near-opaque cream with a gold border; the rain shows faintly through the cream.
export const CROSSING_IMPACT_TILE_BG = 'rgba(252,248,241,0.95)';
// An unselected tile beside a picked one is drawn at this opacity, text included (a documented AA exception, bryan 2026-10-31).
export const CROSSING_IMPACT_TILE_DIM = 0.55;
export const CROSSING_IMPACT_TILE_SELECTED_BG = '#F9F1EC';
export const CROSSING_IMPACT_TILE_SELECTED_BORDER = '#C05F47';
export const CROSSING_IMPACT_TILE_SELECTED_INK = '#7A3A28';
// The reference sets the clue line in #8A7B66, which is 3.9:1 on the cream tile; this is the same warm grey at 5.6:1.
export const CROSSING_IMPACT_TILE_CLUE_INK = '#6E6258';
// The crown tier's fill on 3A, 4A and 4B before Forgive. Read from the v28 prototype (docs/plans/crossing/evidence/design-v28/prototype):
// the Impact step's head tier is filled mkBaseDk (line 5100) = mixh(ba,'#2A3038',0.18) with ba = '#7E8795' (line 5088), which is #6F7784.
export const CROSSING_IMPACT_CROWN_STORM = '#6F7784';
// The drop shadow under the storm crown, the storm-grey copy of the crown tier.
export const CROSSING_IMPACT_CROWN_SHADOW = 'drop-shadow(0 6px 10px rgba(40,44,52,0.16))';
// The crown's words on that grey: the 2D inks are below AA on it, and near-black clears 4.5:1 (4.6:1);
// theme/__tests__/impactTileContrast.test.ts holds the ratio.
export const CROSSING_IMPACT_HEAD_INK = '#000000';

// The halo on the 2D weather vignette scene (v28 wrSceneHalo); larger than CROSSING_HEAD_HALO, which lifts the crown's own text.
export const CROSSING_WEATHER_SCENE_HALO = 'drop-shadow(0 0 10px rgba(255,255,255,0.45)) drop-shadow(0 0 24px rgba(255,255,255,0.28))';

// The 2D weather vignettes (Issue #2166), from the weather response reference
// (docs/plans/crossing/evidence/weather-response-reference/excerpts.md). Values that are gradients are full CSS strings.
export const CROSSING_WEATHER_VIGNETTE = {
  cloudGrowBase: '#C96B52',
  cloudGrowMid: '#D47B60',
  cloudGrowTop: '#DE8C70',
  beamMain: 'linear-gradient(rgba(46,110,100,0.06), rgba(46,110,100,0.5))',
  beamSide: 'linear-gradient(rgba(159,195,186,0.05), rgba(159,195,186,0.4))',
  streakNear: 'rgba(255,255,255,0.62)',
  streakMid: 'rgba(255,255,255,0.5)',
  streakFar: 'rgba(255,255,255,0.42)',
  wheelRim: 'rgba(46,110,100,0.55)',
  wheelSpoke: 'rgba(46,110,100,0.5)',
  wheelHub: 'rgba(230,242,238,0.9)',
  cellA: '#C6C3D6',
  cellATop: '#CFCCDD',
  cellB: '#AEBFC9',
  cellBRain: 'repeating-linear-gradient(96deg, rgba(150,175,190,0.7) 0 2px, transparent 2px 8px)',
  cellLineA: 'rgba(190,167,120,0.95)',
  cellLineB: 'rgba(190,167,120,0.8)',
  cellLineC: 'rgba(190,167,120,0.62)',
  scanBand: 'linear-gradient(rgba(46,110,100,0.04), rgba(46,110,100,0.4))',
  squallBase: '#6E79A4',
  squallTop: '#7E88AF',
  squallRainFar: 'repeating-linear-gradient(100deg, rgba(110,121,164,0.55) 0 2px, transparent 2px 9px)',
  squallRainNear: 'repeating-linear-gradient(100deg, rgba(110,121,164,0.55) 0 1.5px, transparent 1.5px 6px)',
  heldCloudBase: '#9FB4C4',
  heldCloudTop: '#AEC1CF',
  heldBox: '#8A93A8',
  ropeInk: '#6E6258',
  anchorInk: '#4C5A66',
  landmarkNear: '#93A2A8',
  landmarkFar: '#A6B2B6',
  fogBank: 'linear-gradient(90deg, rgba(214,208,222,0) 0%, rgba(214,208,222,0.96) 26%, rgba(214,208,222,0.96) 74%, rgba(214,208,222,0) 100%)',
  fogGlow: 'radial-gradient(circle, rgba(246,222,180,0.75) 0 42%, rgba(246,222,180,0) 72%)',
  frontBase: '#8B98A8',
  frontTop: '#9AA6B4',
  frontRain: 'repeating-linear-gradient(100deg, rgba(139,152,168,0.55) 0 2px, transparent 2px 9px)',
  sun: 'radial-gradient(circle, #F6D48A 0 38%, rgba(246,212,138,0.42) 38% 62%, rgba(246,212,138,0) 78%)',
};

// The 5D summary (Issue #2161): the bird's-eye sky and sea, the v28 compass, and the THIS CROSSING card.
// Values from the design bundle's TOKENS.md and assets/summary/1c-compass-highlighted.svg.
export const CROSSING_SKY_SUMMARY_TOP = '#F3EDE0';
export const CROSSING_SKY_SUMMARY_MID = '#E3E9E8';
export const CROSSING_SKY_SUMMARY_BOTTOM = '#CFDEE0';
export const CROSSING_SUMMARY_HORIZON_LINE = 'rgba(70,119,111,0.35)';
export const CROSSING_SUMMARY_SEA_BASE = '#E6F2EE';
export const CROSSING_SUMMARY_SEA_FILLS = ['#DDEBE6', '#D4E6E0', '#CBE0DA', '#C1D9D3', '#B6D3CC', '#AACCC4', '#9DC4BB', '#8FBDB2'] as const; // the eight swell bands, nearest the horizon first
export const CROSSING_SUMMARY_GLOW = 'radial-gradient(ellipse at 50% 50%, rgba(246,222,180,0.55) 0%, rgba(246,222,180,0.18) 40%, rgba(246,222,180,0) 70%)';
export const COMPASS_WEDGE_FILLS = {
  feeling: '#F3DFA0',
  storm: '#CDD8CB',
  weather: '#E9CFC7',
  reached: '#E4C2BA',
  nextMove: '#F6EEDC',
};
export const COMPASS_WEDGE_LIT_STROKE = '#C9A574';
export const COMPASS_WEDGE_LIT_INK = '#3E3A34';
export const COMPASS_WEDGE_FAINT_FILL = 'rgba(252,248,241,0.09)';
export const COMPASS_WEDGE_FAINT_STROKE = 'rgba(252,248,241,0.35)';
export const COMPASS_FAINT_OUTER_STROKE = 'rgba(252,248,241,0.5)'; // the faint ring just inside the rim
export const COMPASS_WEDGE_FAINT_INK = 'rgba(255,255,255,0.5)';
export const COMPASS_RIM = '#2E4B45';
export const COMPASS_RIM_UNDERLAY = 'rgba(46,75,69,0.45)';
export const COMPASS_NORTH_ARROW = '#D2B06A';
export const CROSSING_GOLD_RIM_TEXT = '#E6C36B';
export const CROSSING_SUMMARY_CARD_GROUND = 'rgba(252,248,241,0.94)';
export const CROSSING_SUMMARY_CARD_BORDER = '#E3D3AF';
export const CROSSING_SUMMARY_CARD_SHADOW = '0 12px 36px rgba(20,60,70,0.22)';
export const CROSSING_SUMMARY_ROW_INK = '#2E3138';
export const CROSSING_SUMMARY_LABEL_INK = '#2E4B45';
export const CROSSING_SUMMARY_DIVIDER = '#E6DCC8';
export const CROSSING_SUMMARY_TRUTH_LABEL = '#B5853C';
export const CROSSING_SUMMARY_CHILD_INK = '#5B5248';
export const CROSSING_EXIT_OVERLAY = '#F7F3EA'; // the send-off's final fade, over the whole stage
